'use client';

import { c as _c } from "react/compiler-runtime";
import { FeatherAlertTriangle } from '@subframe/core';
import { Button, Loader } from '@/shared/components/ui';
import { formatDateTimeFull } from '@/shared/formatters';
import type { AnexoNoRegistro, RegistroDeAnexos } from '../../types/baixarProcesso.types';
import { anexosForaDoPdf, eventoDoAnexo, motivoEmTexto } from '../../utils/anexosDoRegistro';
import { FalhaAoCarregarRegistro } from './FalhaAoCarregarRegistro';
interface AnexosForaDoPdfProps {
  /**
   * Quantos não entraram: aparece antes de a lista chegar. `null` quando só a ciência pendente
   * avisou (pacote lido antes da contagem).
   */
  total: number | null;
  registro: RegistroDeAnexos | null;
  carregando: boolean;
  /** A busca da lista falhou: há o que tentar de novo, e nada de que dar ciência ainda. */
  falhou: boolean;
  cienciaPendente: boolean;
  enviandoCiencia: boolean;
  onDarCiencia: () => void;
  onTentarDeNovo: () => void;
}

// Zero vem do registro que chegou vazio com a ciência pendente: "0 anexos não entraram" se
// contradiz, e o texto genérico é o que vale até a lista aparecer.
function tituloDosAnexosFora(total: number | null): string {
  if (total === null || total === 0) return 'Há anexos que não entraram no PDF';
  return total === 1 ? '1 anexo não entrou no PDF' : `${total} anexos não entraram no PDF`;
}
type ListaDosAnexosForaProps = Pick<AnexosForaDoPdfProps, 'carregando' | 'falhou' | 'onTentarDeNovo'> & {
  registro: RegistroDeAnexos | null;
  fora: AnexoNoRegistro[];
};

/**
 * A lista que chegou, o carregamento, ou o "Tentar de novo". Registro que chegou SEM nenhum anexo
 * fora é tratado como a falha: o bloco só existe porque há o que listar.
 */
function ListaDosAnexosFora(t0) {
  const $ = _c(7);
  const {
    registro,
    fora,
    carregando,
    falhou,
    onTentarDeNovo
  } = t0;
  if (fora.length > 0) {
    let t1;
    if ($[0] !== fora) {
      t1 = fora.map(_temp);
      $[0] = fora;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== t1) {
      t2 = <ul className="m-0 flex list-none flex-col gap-1.5 p-0">{t1}</ul>;
      $[2] = t1;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  if (!carregando && (falhou || registro)) {
    let t1;
    if ($[4] !== onTentarDeNovo) {
      t1 = <FalhaAoCarregarRegistro mensagem={"N\xE3o conseguimos carregar a lista dos anexos."} onTentarDeNovo={onTentarDeNovo} />;
      $[4] = onTentarDeNovo;
      $[5] = t1;
    } else {
      t1 = $[5];
    }
    return t1;
  }
  let t1;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="flex items-center gap-2 text-[12.5px] text-neutral-600" role="status"><Loader size="small" />Carregando os anexos…</div>;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  return t1;
}

/**
 * Os anexos que não entraram no PDF e a ciência de quem baixa (AUT-ANX-001). "Continuar sem anexo
 * exige registro da decisão": o servidor só libera o download depois do "Estou ciente", que fica
 * registrado com o nome de quem deu e quais anexos viu — por isso o botão só fica ativo com a
 * lista na tela. Sem ela (carregando ou falhou), a pessoa daria ciência do que não viu.
 */
function _temp(anexo) {
  return <li className="flex min-w-0 flex-col" key={anexo.anexoId}><span className="text-default-font truncate text-[13px] font-medium">{anexo.nome}</span><span className="text-[11.5px] text-neutral-600">{eventoDoAnexo(anexo.eventoNumero)} · {motivoEmTexto(anexo.motivo)}</span></li>;
}
export function AnexosForaDoPdf(t0) {
  const $ = _c(26);
  const {
    total,
    registro,
    carregando,
    falhou,
    cienciaPendente,
    enviandoCiencia,
    onDarCiencia,
    onTentarDeNovo
  } = t0;
  let t1;
  if ($[0] !== registro) {
    t1 = registro ? anexosForaDoPdf(registro) : [];
    $[0] = registro;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const fora = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <FeatherAlertTriangle className="text-warning-700 mt-px h-[17px] w-[17px] flex-none" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== total) {
    t3 = tituloDosAnexosFora(total);
    $[3] = total;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t3) {
    t4 = <h3 className="text-warning-800 m-0 text-[13.5px] font-semibold" id="anexos-fora-do-pdf">{t3}</h3>;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <span className="text-warning-800 text-[12.5px] leading-[1.6]">No lugar de cada um, o PDF traz uma página de aviso com o motivo. Os arquivos continuam no processo.</span>;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t4) {
    t6 = <div className="flex items-start gap-3">{t2}<div className="flex min-w-0 flex-col gap-0.5">{t4}{t5}</div></div>;
    $[8] = t4;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== carregando || $[11] !== falhou || $[12] !== fora || $[13] !== onTentarDeNovo || $[14] !== registro) {
    t7 = <ListaDosAnexosFora carregando={carregando} falhou={falhou} fora={fora} onTentarDeNovo={onTentarDeNovo} registro={registro} />;
    $[10] = carregando;
    $[11] = falhou;
    $[12] = fora;
    $[13] = onTentarDeNovo;
    $[14] = registro;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== cienciaPendente || $[17] !== enviandoCiencia || $[18] !== fora || $[19] !== onDarCiencia || $[20] !== registro) {
    t8 = cienciaPendente ? <div className="border-warning-200 flex flex-col gap-2.5 border-t border-solid pt-3"><span className="text-warning-800 text-[12.5px] leading-[1.6]">Para baixar, confirme que está ciente de que estes anexos não estão no PDF. A ciência fica registrada no processo.</span><div><Button disabled={enviandoCiencia || fora.length === 0} loading={enviandoCiencia} onClick={onDarCiencia} variant="brand-primary">Estou ciente</Button></div></div> : registro?.cienciaEm ? <span className="text-[12px] text-neutral-600">Ciência registrada em {formatDateTimeFull(registro.cienciaEm)}.</span> : null;
    $[16] = cienciaPendente;
    $[17] = enviandoCiencia;
    $[18] = fora;
    $[19] = onDarCiencia;
    $[20] = registro;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== t6 || $[23] !== t7 || $[24] !== t8) {
    t9 = <section aria-labelledby="anexos-fora-do-pdf" className="border-warning-200 bg-warning-50 flex flex-col gap-3 rounded-[12px] border border-solid px-[18px] py-4">{t6}{t7}{t8}</section>;
    $[22] = t6;
    $[23] = t7;
    $[24] = t8;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  return t9;
}